<template>
  <div class="calendario">
    <a
      v-if="puedeAnadir"
      class="btn btn-cifra btn-new"
      @click.prevent="$emit('event-new')"
    >
      <i class="fa fa-plus"></i>
    </a>
    <ModalSimple v-model="showModal" tamano="sm">
      <input-fecha-hora
        ref="datepicker"
        v-model="fechaDatepicker"
        :inline="true"
        class="m-auto"
      />
    </ModalSimple>
    <FullCalendar
      ref="fullCalendar"
      class="fc5"
      :options="calendarOptions"
    />
  </div>
</template>

<script>
// import '@fullcalendar/core/vdom'; // solves problem with Vite
// import allLocales from '@fullcalendar/core/locales-all';
import caLocale from '@fullcalendar/core/locales/ca';
import esLocale from '@fullcalendar/core/locales/es';
import euLocale from '@fullcalendar/core/locales/eu';
import glLocale from '@fullcalendar/core/locales/gl';
import FullCalendar from '@fullcalendar/vue3';

// Ojo que si cambiamos este plugin de posición, no funciona
import luxon2Plugin from '@fullcalendar/luxon2';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import Overlay from '../../libs/cifra/overlay';
import { onlyISODate } from '../../libs/dates';
import ModalSimple from '../ModalSimple.vue';

export default {
  components: {
    FullCalendar, ModalSimple,
  },
  props: {
    fechaInicial: { type: [String, Date], default: () => new Date() },
    puedeAnadir: { type: Boolean, default: false },
    eventSource: { type: String, default: null },
    slotTodoElDia: { type: Boolean, default: true },
    seleccionable: { type: Boolean, default: true },
  },
  emits: ['event-render', 'event-click', 'dates-selected', 'event-moved', 'event-new'],
  data() {
    const self = this;
    const eventSources = this.eventSource ? [this.eventSource] : undefined;
    return {
      showModal: false,
      fechaDatepicker: onlyISODate(new Date()),
      calendarOptions: {
        initialDate: this.fechaInicial,
        allDaySlot: this.slotTodoElDia,
        height: '100%',
        eventTextColor: '#333',
        selectable: this.seleccionable,
        plugins: [luxon2Plugin, interactionPlugin, dayGridPlugin, timeGridPlugin, listPlugin],
        initialView: 'timeGridWeek',
        locales: [caLocale, esLocale, euLocale, glLocale],
        locale: this.$I18n.currentLocale(),
        timeZone: this.$timeZone,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,listWeek',
        },
        eventSources,
        // OJO!!!!!! El this de los eventos es el propio fullcalendar no nuestra clase
        windowResize: (_arg) => {
          self.asignarTamano();
        },
        viewDidMount: (_arg) => {
          self.asignarTamano();
        },
        eventDidMount: (arg) => {
          self.$emit('event-render', arg);
        },
        eventClick: (info) => {
          const esAnotacion = info.event.classNames.includes('anotacion-diario');
          if (info.event.url && !esAnotacion) {
            // Va a navegar a la URL, ponemos overlay
            const overlay = new Overlay('body');
            overlay.show();
            /* global $ */
            // Cuando turbolinks cargue la página, quitamos el overlay
            $(document).one('turbo:load', () => overlay.hide());
          } else {
            self.$emit('event-click', info);
          }
        },
        // Cuando hacen click en un cuadro de una fecha/hora o seleccionan un rango
        select: (selectionInfo) => {
          self.$emit('dates-selected', selectionInfo);
        },
        eventDrop: this.emitEventoMovido,
        eventResize: this.emitEventoMovido,
        eventResizeStop: this._ocultarTooltips,
        loading: (isLoading) => {
          this._ocultarTooltips();
          this.bloquearPrevNext(isLoading);
        },
        views: {
          week: {
            dayHeaderContent: (info) => {
              const diaSemana = this.getCalendar().formatDate(info.date, { weekday: 'short' });
              // En caso de que necesitemos usar el objeto date, hay que transformalo primero
              // con luxon usando el plugin de luxon de fullcalendar:
              // date = toLuxonDateTime(info.date, this.getCalendar())
              const dia = this.getCalendar().formatDate(info.date, { day: 'numeric' });
              const classes = info.isToday ? 'hoy' : 'no-hoy';
              return { html: `<div class="dia-semana ${classes}">${diaSemana}</div><div class="dia ${classes}">${dia}</div>` };
            },
          },
        },
      },
    };
  },
  watch: {
    eventSource() {
      this.getCalendar().setOption('eventSources', this.eventSource ? [this.eventSource] : undefined);
    },
    fechaDatepicker(value) {
      this.getCalendar().gotoDate(value);
      this.showModal = false;
    },
  },
  mounted() {
    const title = this.$el.querySelector('.fc-toolbar-title');
    title.classList.add('clickable');
    title.addEventListener('click', () => {
      this.showModal = true;
    });
  },
  methods: {
    getCalendar() {
      return this.$refs.fullCalendar.getApi();
    },
    asignarTamano() {
      let height = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
      // Posición superior actual del div del calendario
      const topOffset = this.$el.getBoundingClientRect().top + window.scrollY;
      // Altura de márgenes
      const footerMargenes = 20;
      height -= topOffset;
      height -= footerMargenes;
      this.getCalendar().setOption('height', height);
    },
    // Info tiene un objeto info.event con los datos y la funcion info.revert
    emitEventoMovido(info) {
      this._ocultarTooltips();
      this.$emit('event-moved', info);
    },
    // Eliminamos los tooltips activos para que no se queden "colgados"
    // después de las acciones sobre los eventos
    _ocultarTooltips() {
      $('.tooltip').hide();
    },
    bloquearPrevNext(bloquear) {
      const fcButtonGroup = this.$el.querySelector('.fc-button-group');
      if (!fcButtonGroup) {
        return;
      }

      const buttons = fcButtonGroup.querySelectorAll('.fc-button');
      buttons.forEach((boton) => {
        if (bloquear) {
          boton.setAttribute('disabled', '');
        } else {
          boton.removeAttribute('disabled');
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "../../../assets/stylesheets/cifra/vars/main.scss";

.calendario {
  .uneditable,
  .uneditable a {
    cursor: initial;
  }

  .fc .fc-daygrid-day.fc-day-today,
  .fc .fc-timegrid-col.fc-day-today {
    background-color: transparent;
  }

  .fc-v-event,
  .fc-h-event,
  .fc table.fc-scrollgrid,
  .fc-scrollgrid-section > th,
  .fc .fc-timegrid-slot-minor,
  th.fc-col-header-cell,
  th.fc-timegrid-axis {
    border: none;
    border-top-style: none !important;
  }

  // Altula de las celdas donde van los eventos. Las hacemos más grandes
  // y un poco más grande el texto
  .fc .fc-timegrid-slot {
    height: 2em;
  }
  .fc .fc-event-title {
    font-size: var(--fc-small-font-size, 1em);
  }

  // Cabeceras de los días y el día actual
  .fc-col-header-cell {
    .dia-semana {
      text-transform: uppercase;
      font-size: 0.8em;
      line-height: 32px;
      &.hoy {
        color: $cifra-color;
      }
    }
    .dia {
      font-size: 1.6em;
      font-weight: normal;
      letter-spacing: -1.5px;
      text-indent: -1.5px;
      border-radius: 100%;
      line-height: 46px;
      height: 46px;
      margin-left: auto;
      margin-right: auto;
      margin-top: -8px;
      width: 46px;

      &.hoy {
        background-color: $cifra-color;
        color: white;
      }
    }
  }

  // Botones color cifra
  $color-botones: $cifra-color;
  $color-texto-botones: #FFFFFF;
  $color-borde-botones: darken($color: $color-botones, $amount: 5);
  $color-boton-activo: darken($color-botones, $amount: 15);

  .fc .fc-button,
  .fc .fc-button.fc-button-primary {
    background-color: $color-botones;
    border-color: $color-borde-botones;
    color: $color-texto-botones;
  }
  .fc .fc-button-primary:not(:disabled):active,
  .fc .fc-button-primary:not(:disabled).fc-button-active {
    background-color: $color-boton-activo;
    border-color: $color-boton-activo;
  }
}

// Traído tal cual estaba, sobrará el 90% de este css
.calendario {
  .btn-new {
    font-weight: 500;
    font-size: 16px;
    cursor: pointer;
    position: fixed;
    right: 50px;
    bottom: 70px;
    padding: 0 6px;
    background-color: $cifra-color;
    color: #ffffff;
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.26);
    z-index: 200;
    line-height: 56px;
    width: 56px;
    height: 56px;
    -webkit-border-radius: 50% !important;
    -moz-border-radius: 50% !important;
    -ms-border-radius: 50% !important;
    border-radius: 50% !important;
  }

  .fc-event-diario {
    line-height: 12px;
    padding: 2px 5px;
    position: absolute;
    left: 0;
    top: 2px;
    cursor: pointer;
    z-index: 100;

    .fa-comment {
      color: $cifra-color;
      font-size: 1.2em;
    }
  }
  .fc-event-title,
  .fc-list-event-title {
    .fa {
      font-size: 0.85em;
      vertical-align: unset;
      margin-right: 4px;
    }
  }
  // Para que si no cabe algo en la cajita del evento, no salga el texto por ahí volando
  .fc-event-title-container {
    overflow: hidden;
  }
}
</style>
